import React from 'react';

import { UxContext } from '@client/context/UxContext';
import useIsFormDirty from '@client/hooks/useIsFormDirty';
import useSectionData from '@client/hooks/useSectionData';
import i18n from '@client/utils/i18n';
import { RecipeSection } from '@ledium/types';
import { CircadianChart, LediumForm } from '@ledium/ui-common';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Container } from '@mui/material';
import './sectionEditor.scss';

import SectionAddNewEventButton from './SectionAddNewEventButton';
import SectionCircadianEventList from './SectionCircadianEventList';
import SectionCircadianLengthSelect from './SectionCircadianLengthSelect';
import SectionColorPicker from './SectionColorPicker';
import SectionNameInput from './SectionNameInput';
import SectionRepeatNumInput from './SectionRepeatNumInput';
import SectionTimelineInfo from './SectionTimelineInfo';

interface SectionEditorProps {
    section: RecipeSection;
    onSave: (sectionData: RecipeSection) => void;
    onCancel: () => void;
    onDelete: (section: RecipeSection) => void;
    onDirtyChange?: (isDirty: boolean) => void;
    readOnly: unknown;
}

const SectionEditor = ({ section, onSave, onCancel, onDelete, onDirtyChange, readOnly }: SectionEditorProps) => {
    const { handleAlert } = React.useContext(UxContext)!;
    const { sectionData, setSectionData, chartData } = useSectionData(section);
    const isDirty = useIsFormDirty(sectionData);

    React.useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    function onSubmit() {
        onSave(sectionData);
    }

    function onChange(event: React.ChangeEvent<HTMLInputElement>) {
        let value: string | number = event.target.value;
        const { name, type } = event.target;
        if (type === 'number') {
            value = parseInt(value, 10);
        }
        setSectionData({ ...sectionData, [name]: value });
    }

    function handleCancel() {
        if (isDirty) {
            handleAlert({
                title: i18n('discard_section_changes_title') as string,
                message: i18n('discard_section_changes_message') as string,
                okCallback: onCancel
            });
        } else {
            onCancel();
        }
    }

    function confirmDeleteSection() {
        handleAlert({
            title: i18n('delete_section_title') as string,
            message: i18n('delete_section_message') as string,
            destructive: true,
            okCallback: () => {
                onDelete(section);
            }
        });
    }

    if (!section) {
        return null;
    }

    return (
        <Container>
            <LediumForm
                isDirtyOverride={isDirty}
                onSubmit={onSubmit}
                saveText={i18n('save_section')}
                showButtons={true}
                onCancel={handleCancel}
                saveDisabled={!!(sectionData.circadianEvents.length && !sectionData.circadianEvents.every((event) => event.lightRecipeId))}
                additionalButton={
                    readOnly ? null : (
                        <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={confirmDeleteSection}>
                            {i18n('delete_section_title')}
                        </Button>
                    )
                }
            >
                <SectionNameInput sectionData={sectionData} onChange={onChange} />
                <SectionRepeatNumInput sectionData={sectionData} onChange={onChange} />
                <SectionCircadianLengthSelect
                    sectionData={sectionData}
                    onChange={(e) => setSectionData({ ...sectionData, circadianLength: Number(e.target.value) })}
                />
                <SectionColorPicker sectionData={sectionData} setSectionData={setSectionData} />
                <CircadianChart data={chartData} />
                <SectionTimelineInfo />
                <SectionCircadianEventList sectionData={sectionData} setSectionData={setSectionData} />
                <SectionAddNewEventButton sectionData={sectionData} setSectionData={setSectionData} />
            </LediumForm>
        </Container>
    );
};

export default SectionEditor;
